import { AUTHENTICATION_STRENGTH_REQUIREMENT_REF_PREFIX } from './requirementRef.util'
import { NAMED_LOCATION_CONTENT_CHOICE } from './namedLocationRequirement.util'

// "authStrength:<name>" is this domain's own sentinel prefix, carried in
// grantControls.authenticationStrength.id in place of a tenant-specific custom strength id.
export const REQUIREMENT_REF_PREFIX = AUTHENTICATION_STRENGTH_REQUIREMENT_REF_PREFIX

// Template meta_data key holding the custom authentication strength the template requires:
// { name, allowedCombinations }. Mirrors the backend's authenticationStrengthRequirementUtils.js.
export const AUTHENTICATION_STRENGTH_REQUIREMENT_META_KEY = 'authenticationStrengthRequirement'

export const isRequirementRef = (value) => typeof value === 'string' && value.startsWith(REQUIREMENT_REF_PREFIX)

export const getRequirementId = (value) => (isRequirementRef(value) ? value.slice(REQUIREMENT_REF_PREFIX.length) : null)

export const toRequirementRef = (requirementId) => `${REQUIREMENT_REF_PREFIX}${requirementId}`

export const getAuthenticationStrengthRequirementId = (policyBody) =>
  getRequirementId(policyBody?.grantControls?.authenticationStrength?.id)

// Mirrors the backend's resolveAuthenticationStrengthRequirement.js RESOLUTION_STATUS.
export const AUTHENTICATION_STRENGTH_REQUIREMENT_STATUS = {
  MATCHED: 'matched',
  CREATABLE: 'creatable',
  MULTI_MATCH: 'multiMatch',
  UNRESOLVABLE: 'unresolvable',
}

// The MSP's choice when a matched strength's combinations differ from the template (AC4). The
// values are the named location ones, since both travel in the deploy's shared contentChoices map.
export const AUTHENTICATION_STRENGTH_CONTENT_CHOICE = NAMED_LOCATION_CONTENT_CHOICE

// The allowed combinations a custom authentication strength can be built from — Graph's
// authenticationMethodModes (identity/conditionalAccess/authenticationStrength/authenticationMethodModes)
// — each mapped to the message holding Entra's own label for it. Every tenant offers the same list,
// so it is kept here rather than fetched per tenant.
const COMBINATION_MESSAGE_IDS = {
  windowsHelloForBusiness: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_WINDOWS_HELLO_FOR_BUSINESS',
  fido2: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_FIDO2',
  x509CertificateMultiFactor: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_X509_CERTIFICATE_MULTI_FACTOR',
  deviceBasedPush: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_DEVICE_BASED_PUSH',
  temporaryAccessPassOneTime: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_TEMPORARY_ACCESS_PASS_ONE_TIME',
  temporaryAccessPassMultiUse: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_TEMPORARY_ACCESS_PASS_MULTI_USE',
  'password,microsoftAuthenticatorPush':
    'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_PASSWORD_MICROSOFT_AUTHENTICATOR_PUSH',
  'password,softwareOath': 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_PASSWORD_SOFTWARE_OATH',
  'password,hardwareOath': 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_PASSWORD_HARDWARE_OATH',
  'password,sms': 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_PASSWORD_SMS',
  'password,voice': 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_PASSWORD_VOICE',
  federatedMultiFactor: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_FEDERATED_MULTI_FACTOR',
  'microsoftAuthenticatorPush,federatedSingleFactor':
    'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_MICROSOFT_AUTHENTICATOR_PUSH_FEDERATED_SINGLE_FACTOR',
  'softwareOath,federatedSingleFactor':
    'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_SOFTWARE_OATH_FEDERATED_SINGLE_FACTOR',
  'hardwareOath,federatedSingleFactor':
    'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_HARDWARE_OATH_FEDERATED_SINGLE_FACTOR',
  'sms,federatedSingleFactor': 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_SMS_FEDERATED_SINGLE_FACTOR',
  'voice,federatedSingleFactor': 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_VOICE_FEDERATED_SINGLE_FACTOR',
  x509CertificateSingleFactor: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_X509_CERTIFICATE_SINGLE_FACTOR',
  qrCodePin: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_QR_CODE_PIN',
  sms: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_SMS',
  password: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_PASSWORD',
  federatedSingleFactor: 'POLICY_TEMPLATES.AUTHENTICATION_STRENGTH_COMBINATION_FEDERATED_SINGLE_FACTOR',
}

export const AUTHENTICATION_STRENGTH_COMBINATIONS = Object.keys(COMBINATION_MESSAGE_IDS)

// A combination Graph adds later has no label yet, so it shows as Graph's own value.
export const formatAuthenticationStrengthCombination = (intl, combination) =>
  COMBINATION_MESSAGE_IDS[combination] ? intl.formatMessage({ id: COMBINATION_MESSAGE_IDS[combination] }) : combination

export const formatAuthenticationStrengthCombinations = (intl, combinations = []) =>
  combinations.map((combination) => formatAuthenticationStrengthCombination(intl, combination)).join(', ')

// The definition to save with a template, kept only while its policyBody still references it —
// an edit that switched to a built-in strength (or cleared it) drops the stale definition.
export const getAuthenticationStrengthRequirementMeta = (policyBody, definition) => {
  const requirementId = getAuthenticationStrengthRequirementId(policyBody)
  if (!requirementId || definition?.name !== requirementId) {
    return {}
  }
  return { [AUTHENTICATION_STRENGTH_REQUIREMENT_META_KEY]: definition }
}
